import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

import { CURRENCY_CONFIG, COUNTRY_TO_CURRENCY } from 'constants/currencies';

const STORAGE_KEY = 'app.activeCurrency';
const DEFAULT_CURRENCY = 'ARS';

const CurrencyContext = createContext(null);

const ALL_AVAILABLE = Object.entries(CURRENCY_CONFIG).map(([currency, meta]) => ({
  currency,
  ...meta,
}));

const enrich = (iso) => ({
  currency: iso,
  country: CURRENCY_CONFIG[iso]?.country ?? iso,
  flag:    CURRENCY_CONFIG[iso]?.flag    ?? null,
  label:   CURRENCY_CONFIG[iso]?.label   ?? iso,
});

/**
 * allowedCountries: optional string[] of country ISOs from the login response
 * (e.g. ['BR', 'AR']). When provided, restricts the available currencies and
 * auto-selects the first allowed one if the current active is not in the list.
 */
export const CurrencyProvider = ({ children, allowedCountries }) => {
  // Derive allowed currency ISOs from the countries list; null = no restriction
  const allowedCurrencies = useMemo(() => {
    if (!allowedCountries?.length) return null;
    const mapped = allowedCountries.map((c) => COUNTRY_TO_CURRENCY[c]).filter(Boolean);
    return mapped.length ? mapped : null;
  }, [allowedCountries]);

  const available = useMemo(() => {
    if (!allowedCurrencies) return ALL_AVAILABLE;
    return ALL_AVAILABLE.filter(({ currency }) => allowedCurrencies.includes(currency));
  }, [allowedCurrencies]);

  const [activeCurrencyIso, setActiveCurrencyIso] = useState(() => {
    const stored = localStorage.getItem(STORAGE_KEY);
    return stored && CURRENCY_CONFIG[stored] ? stored : DEFAULT_CURRENCY;
  });

  // When the merchant's allowed countries change (login/logout), enforce the restriction
  useEffect(() => {
    if (!allowedCurrencies) return;
    setActiveCurrencyIso((prev) => {
      if (allowedCurrencies.includes(prev)) return prev;
      const first = available[0]?.currency ?? DEFAULT_CURRENCY;
      localStorage.setItem(STORAGE_KEY, first);
      return first;
    });
  }, [allowedCurrencies, available]);

  const setActiveCurrency = useCallback((iso) => {
    if (!iso || !CURRENCY_CONFIG[iso]) return;
    if (allowedCurrencies && !allowedCurrencies.includes(iso)) return;
    localStorage.setItem(STORAGE_KEY, iso);
    setActiveCurrencyIso(iso);
  }, [allowedCurrencies]);

  const value = useMemo(() => ({
    /** Full object for the active selection: { currency, country, flag, label } */
    active: enrich(activeCurrencyIso),

    /** Full objects for available options (filtered by merchant countries when applicable) */
    available,

    /** Change active currency by ISO string, e.g. setActiveCurrency('BRL') */
    setActiveCurrency,
  }), [activeCurrencyIso, available, setActiveCurrency]);

  return (
    <CurrencyContext.Provider value={value}>
      {children}
    </CurrencyContext.Provider>
  );
};

export const useCurrency = () => {
  const context = useContext(CurrencyContext);
  if (!context) {
    throw new Error('useCurrency must be used within CurrencyProvider');
  }
  return context;
};
